import { publish } from '@theme/ap-analytics';

/**
 * AP Lookbook Gallery — windowed fullscreen lightbox (v2).
 *
 * Replaces the previous Horizon `slideshow-component`-based modal. Per-look data is read from
 * JSON islands (`.ap-lookbook-gallery__look-data`); slides are built on demand into a bounded
 * 3-node rail ([prev | current | next]) that recycles nodes and preloads neighbours on navigation.
 *
 * Deliberately has NO scroll listener, NO ResizeObserver, NO per-touch layout measurement, and a
 * single pagination controller — this is what fixes the v1 stutter. Look count is the true count
 * (no 50-cap), so looping only wraps at the real end.
 */
class ApLookbookGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.modal = this.querySelector('[data-ap-lookbook-modal]');
    this.rail = this.modal && this.modal.querySelector('[data-ap-lookbook-rail]');
    this.live = this.modal && this.modal.querySelector('[data-ap-lookbook-live]');

    if (!this.modal || !this.rail) return;

    this.looks = this.#parseLooks();
    this.total = this.looks.length;
    if (this.total === 0) return;

    // Caption rendering config emitted server-side (keeps v1 markup/classes so merchant custom_css applies)
    this.captionClasses = this.modal.dataset.captionClasses || '';
    this.captionStyle = this.modal.dataset.captionStyle || '';
    this.lookNumberLabel = this.modal.dataset.lookNumberLabel || 'Look APNUMBER';
    this.showLookNumber = this.modal.dataset.showLookNumber !== 'false';
    // 'metaobject' = use each look's Look number field; 'auto' = number by render position (index + 1).
    this.lookNumberSource = this.modal.dataset.lookNumberSource === 'auto' ? 'auto' : 'metaobject';

    this.current = 0;
    this.trigger = null;
    this.isClosing = false;
    this.isAnimating = false;
    // Track the in-flight nav animation so it can be cancelled on open/close/disconnect.
    this.pendingTimer = 0;
    this.pendingOnEnd = null;
    this.bodyLocked = false;
    this.rootHadScrollLock = false;
    this.preloaded = new Set();
    /** @type {HTMLElement[]} [left, center, right] */
    this.railNodes = [];
    // Arrow viewports (>= 990px, see the section CSS) fade between looks; smaller (touch) ones slide.
    this.fadeMatch = window.matchMedia('(min-width: 990px)');

    this.#bindGalleryEvents();
    this.#bindModalEvents();
    this.#bindSwipe();
  }

  disconnectedCallback() {
    this.#abort();
    this.#unlockBody();
  }

  // ---- data ---------------------------------------------------------------

  #parseLooks() {
    const looks = [];
    this.querySelectorAll('.ap-lookbook-gallery__look-data').forEach((node) => {
      try {
        const data = JSON.parse(node.textContent);
        if (data && typeof data.index === 'number') looks[data.index] = data;
      } catch (error) {
        /* ignore a malformed island rather than break the whole lightbox */
      }
    });
    return looks.filter(Boolean);
  }

  #mod(index) {
    return ((index % this.total) + this.total) % this.total;
  }

  #prefersReducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /**
   * The number to display for a look: its metaobject Look number, or — in 'auto' mode — its
   * 1-based position in the gallery. Returns null when there's nothing to show.
   */
  #lookNumber(look) {
    if (!look) return null;
    if (this.lookNumberSource === 'auto') {
      return typeof look.index === 'number' ? look.index + 1 : null;
    }
    return look.lookNumber != null && look.lookNumber !== '' ? look.lookNumber : null;
  }

  /**
   * Caption entries ({title, url|null}) for a look. When the look's `product_names` list is
   * authored it is the source of truth: every name renders, linked when it matches a valid
   * product's title (case-insensitive), plain text otherwise — so names of draft/archived
   * products still appear. Valid products whose title matches no authored name are appended
   * as a safety net. Without authored names, the valid products render as links, as before.
   */
  #captionEntries(look) {
    const products = Array.isArray(look.products) ? look.products : [];
    const names = Array.isArray(look.productNames)
      ? look.productNames.map((name) => (name || '').trim()).filter(Boolean)
      : [];
    if (!names.length) return products.map((product) => ({ title: product.title, url: product.url }));

    const unmatched = [...products];
    const entries = names.map((name) => {
      const key = name.toLowerCase();
      const matchIndex = unmatched.findIndex(
        (product) => (product.title || '').trim().toLowerCase() === key
      );
      if (matchIndex === -1) return { title: name, url: null };
      const [product] = unmatched.splice(matchIndex, 1);
      return { title: name, url: product.url };
    });
    return entries.concat(unmatched.map((product) => ({ title: product.title, url: product.url })));
  }

  // ---- slide construction -------------------------------------------------

  #createSlide(look) {
    const slide = document.createElement('div');
    slide.className = 'ap-lookbook-gallery__modal-slide';
    this.#fillSlide(slide, look);
    return slide;
  }

  #fillSlide(slide, look) {
    if (!look) {
      slide.replaceChildren();
      return;
    }

    const ratio = look.image && look.image.ratio ? look.image.ratio : 0.75;

    const figure = document.createElement('figure');
    figure.className = 'ap-lookbook-gallery__modal-figure';
    figure.style.setProperty('--ap-lookbook-modal-image-ratio', String(ratio));

    const mediaTag = look.singleProductUrl ? 'a' : 'div';
    const media = document.createElement(mediaTag);
    media.className = 'ap-lookbook-gallery__modal-media';
    if (look.singleProductUrl) {
      media.classList.add('ap-lookbook-gallery__modal-media--link');
      media.href = look.singleProductUrl;
    }

    if (look.image && look.image.src) {
      const img = document.createElement('img');
      img.alt = look.alt || '';
      img.decoding = 'async';
      if (look.image.srcset) img.srcset = look.image.srcset;
      if (look.image.sizes) img.sizes = look.image.sizes;
      img.src = look.image.src;
      media.appendChild(img);

      // Skeleton + loading pulse until the image is ready (also reserves layout so the
      // caption doesn't jump). Clear immediately for already-cached images.
      media.classList.add('is-loading');
      if (img.complete && img.naturalWidth > 0) {
        media.classList.remove('is-loading');
      } else {
        const settle = () => media.classList.remove('is-loading');
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      }
    }
    figure.appendChild(media);

    const lookNumber = this.#lookNumber(look);
    const hasLookNumber = this.showLookNumber && lookNumber != null && lookNumber !== '';
    const captionEntries = this.#captionEntries(look);

    if (hasLookNumber || captionEntries.length) {
      const caption = document.createElement('figcaption');
      caption.className = ('ap-lookbook-gallery__modal-caption ' + this.captionClasses).trim();
      if (this.captionStyle) caption.setAttribute('style', this.captionStyle);

      // "Look N" — the pagination indicator, in the same caption slot/style as before.
      if (hasLookNumber) {
        const number = document.createElement('p');
        number.className = 'ap-lookbook-gallery__modal-look-number';
        number.textContent = this.lookNumberLabel.replace('APNUMBER', lookNumber);
        caption.appendChild(number);
      }

      if (captionEntries.length) {
        const products = document.createElement('p');
        products.className = 'ap-lookbook-gallery__modal-products';
        captionEntries.forEach((entry, index) => {
          let node;
          if (entry.url) {
            node = document.createElement('a');
            node.href = entry.url;
          } else {
            node = document.createElement('span');
          }
          node.className = 'ap-lookbook-gallery__modal-product';
          node.textContent = entry.title;
          products.appendChild(node);
          if (index < captionEntries.length - 1) products.appendChild(document.createTextNode(', '));
        });
        caption.appendChild(products);
      }

      figure.appendChild(caption);
    }

    slide.replaceChildren(figure);
  }

  // ---- rail + transforms --------------------------------------------------

  #buildRail(centerIndex) {
    this.current = this.#mod(centerIndex);
    this.railNodes = [];

    if (this.total === 1) {
      const node = this.#createSlide(this.looks[this.current]);
      this.rail.replaceChildren(node);
      this.railNodes = [node];
      this.rail.style.transform = 'translate3d(0, 0, 0)';
      this.#syncInert();
      return;
    }

    const indices = [this.#mod(this.current - 1), this.current, this.#mod(this.current + 1)];
    const nodes = indices.map((index) => this.#createSlide(this.looks[index]));
    this.rail.replaceChildren(...nodes);
    this.railNodes = nodes;
    this.#resetTransform();
    this.#syncInert();
  }

  /**
   * Only the centred look is interactive. The off-screen prev/next rail nodes carry focusable
   * product links, so mark them `inert` to keep them out of the tab order and the accessibility
   * tree (WCAG 2.4.3) — otherwise Tab walks into a neighbour and scrolls it into view. The rail
   * is translated to show the middle node, so the current look is always railNodes[1] (or the
   * sole node when total === 1).
   */
  #syncInert() {
    this.railNodes.forEach((node, index) => {
      node.inert = this.railNodes.length > 1 && index !== 1;
    });
  }

  /** Snap the rail to the centred (resting) position with no animation. */
  #resetTransform() {
    if (this.total < 2) {
      this.rail.style.transform = 'translate3d(0, 0, 0)';
      return;
    }
    this.rail.style.transition = 'none';
    this.rail.style.transform = 'translate3d(-100%, 0, 0)';
    void this.rail.offsetWidth; // force reflow so the next change animates
    this.rail.style.transition = '';
  }

  /** Animate the rail back to the resting position (used to cancel an incomplete swipe). */
  #snapBack() {
    this.rail.style.transition = '';
    this.rail.style.transform = 'translate3d(-100%, 0, 0)';
  }

  /**
   * Recycle the off-screen rail node into the new neighbour and advance `current`.
   * @param {1 | -1} direction
   */
  #recycle(direction) {
    if (direction === 1) {
      const newCurrent = this.#mod(this.current + 1);
      const node = this.railNodes.shift();
      this.#fillSlide(node, this.looks[this.#mod(newCurrent + 1)]);
      this.rail.appendChild(node);
      this.railNodes.push(node);
      this.current = newCurrent;
    } else {
      const newCurrent = this.#mod(this.current - 1);
      const node = this.railNodes.pop();
      this.#fillSlide(node, this.looks[this.#mod(newCurrent - 1)]);
      this.rail.insertBefore(node, this.rail.firstChild);
      this.railNodes.unshift(node);
      this.current = newCurrent;
    }
    this.#syncInert();
  }

  /** Whether the current viewport fades between looks (arrow viewports) vs slides (touch). */
  #isFadeMode() {
    return this.fadeMatch ? this.fadeMatch.matches : false;
  }

  /** Move to the new look + refresh chrome. Transform reset is instant. */
  #commit(direction) {
    this.#recycle(direction);
    this.#resetTransform();
    this.#afterNavigate();
  }

  /**
   * Navigate one step.
   * @param {1 | -1} direction
   */
  #go(direction) {
    if (this.total < 2 || this.isAnimating) return;

    if (this.#prefersReducedMotion()) {
      this.#commit(direction);
      return;
    }

    this.isAnimating = true;

    // Desktop / arrow viewports: fade the look (image + text) out then in, mirroring the overlay's
    // own fade transitions (out = overlay close, in = overlay open; --ap-lookbook-fade-* on the rail).
    if (this.#isFadeMode()) {
      let phase = 'out';
      let settled = false;
      const onEnd = (event) => {
        if (event && (event.target !== this.rail || event.propertyName !== 'opacity')) return;
        if (phase === 'out') {
          phase = 'in';
          this.#commit(direction);
          this.rail.style.transition = 'opacity var(--ap-lookbook-fade-in)';
          requestAnimationFrame(() => {
            this.rail.style.opacity = '1';
          });
          return;
        }
        if (settled) return;
        settled = true;
        this.rail.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
        this.rail.style.opacity = '';
        this.rail.style.transition = '';
        this.isAnimating = false;
      };
      const fallback = window.setTimeout(() => {
        this.rail.removeEventListener('transitionend', onEnd);
        if (phase === 'out') this.#commit(direction);
        this.rail.style.opacity = '';
        this.rail.style.transition = '';
        this.isAnimating = false;
      }, 1200);
      this.rail.addEventListener('transitionend', onEnd);
      this.pendingTimer = fallback;
      this.pendingOnEnd = onEnd;

      this.rail.style.transition = 'opacity var(--ap-lookbook-fade-out)';
      this.rail.style.opacity = '0';
      return;
    }

    // Touch / no-arrow viewports: slide.
    this.rail.style.transition = '';
    this.rail.style.transform = direction === 1 ? 'translate3d(-200%, 0, 0)' : 'translate3d(0, 0, 0)';

    let settled = false;
    const onEnd = (event) => {
      if (event && (event.target !== this.rail || event.propertyName !== 'transform')) return;
      if (settled) return;
      settled = true;
      this.rail.removeEventListener('transitionend', onEnd);
      window.clearTimeout(fallback);
      this.#commit(direction);
      this.isAnimating = false;
    };
    const fallback = window.setTimeout(() => onEnd(), 600);
    this.rail.addEventListener('transitionend', onEnd);
    this.pendingTimer = fallback;
    this.pendingOnEnd = onEnd;
  }

  /** Cancel any in-flight nav animation and reset the rail to a clean state. */
  #abort() {
    if (this.pendingTimer) {
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = 0;
    }
    if (this.pendingOnEnd) {
      this.rail.removeEventListener('transitionend', this.pendingOnEnd);
      this.pendingOnEnd = null;
    }
    this.isAnimating = false;
    this.rail.style.opacity = '';
    this.rail.style.transition = '';
  }

  #afterNavigate() {
    this.#announce();
    // Navigating between looks is a view, not a fresh modal open — keep it a distinct
    // event so `ap_look_opened` counts actual opens rather than every slide change.
    this.#publishAnalytics('ap_look_viewed');
    this.#preload(this.#mod(this.current + 1));
    this.#preload(this.#mod(this.current - 1));
  }

  #announce() {
    if (!this.live) return;
    const look = this.looks[this.current];
    if (!look) return;
    const parts = [];
    const lookNumber = this.#lookNumber(look);
    if (lookNumber != null && lookNumber !== '') {
      parts.push(this.lookNumberLabel.replace('APNUMBER', lookNumber));
    }
    if (look.name) parts.push(look.name);
    this.live.textContent = parts.join(' — ') || String(this.current + 1);
  }

  #preload(index) {
    const look = this.looks[index];
    if (!look || !look.image || !look.image.src || this.preloaded.has(index)) return;
    this.preloaded.add(index);

    const image = new Image();
    if (look.image.srcset) image.srcset = look.image.srcset;
    if (look.image.sizes) image.sizes = look.image.sizes;
    image.decoding = 'async';
    image.src = look.image.src;
  }

  #publishAnalytics(eventName) {
    const look = this.looks[this.current];
    if (!look) return;
    publish(eventName, {
      look_name: look.name || '',
      look_number: look.lookNumber != null ? look.lookNumber : null,
      product_ids: (look.products || []).map((product) => product.id).filter(Boolean),
    });
  }

  // ---- open / close -------------------------------------------------------

  #triggerIndex(trigger) {
    if (!trigger) return -1;

    const raw = parseInt(trigger.getAttribute('data-ap-lookbook-slide-index'), 10);
    const lookId = trigger.getAttribute('data-look-id');

    if (!Number.isNaN(raw) && raw >= 0 && raw < this.total) {
      if (!lookId || (this.looks[raw] && this.looks[raw].lookId === lookId)) return raw;
    }

    if (lookId) {
      const found = this.looks.findIndex((look) => look.lookId === lookId);
      if (found >= 0) return found;
    }

    return Number.isNaN(raw) ? -1 : this.#mod(raw);
  }

  #open(trigger) {
    if (typeof this.modal.showModal !== 'function') return;

    const index = this.#triggerIndex(trigger);
    if (index < 0) return;

    this.trigger = trigger;
    this.isClosing = false;
    this.modal.classList.remove('dialog-closing');

    this.#abort(); // clear any leftover animation state from a prior interrupted close
    this.#lockBody();
    this.#buildRail(index);

    this.modal.showModal();

    this.#announce();
    this.#publishAnalytics('ap_look_opened');
    this.#preload(this.#mod(index + 1));
    this.#preload(this.#mod(index - 1));

    const closeButton = this.modal.querySelector('[data-ap-lookbook-close]');
    if (closeButton && typeof closeButton.focus === 'function') {
      closeButton.focus({ preventScroll: true });
    }
  }

  #close() {
    if (!this.modal.open || this.isClosing) return;

    this.isClosing = true;
    this.modal.classList.add('dialog-closing');

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      this.modal.removeEventListener('animationend', onEnd);
      window.clearTimeout(fallback);
      this.modal.close();
    };
    const onEnd = (event) => {
      if (event.target !== this.modal) return;
      finish();
    };
    const fallback = window.setTimeout(finish, 600);
    this.modal.addEventListener('animationend', onEnd);
  }

  // ---- events -------------------------------------------------------------

  #bindGalleryEvents() {
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-ap-lookbook-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      event.preventDefault();
      this.#open(trigger);
    });

    const preload = (event) => {
      const trigger = event.target.closest('[data-ap-lookbook-trigger]');
      if (!trigger) return;
      const index = this.#triggerIndex(trigger);
      if (index >= 0) this.#preload(index);
    };

    this.addEventListener('pointerover', preload);
    this.addEventListener('focusin', preload);
    this.addEventListener('touchstart', preload, { passive: true });
  }

  #bindModalEvents() {
    this.modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-ap-lookbook-close]')) {
        this.#close();
        return;
      }
      // Let interactive content and navigation handle their own clicks.
      if (
        event.target.closest('[data-ap-lookbook-arrow]') ||
        event.target.closest('a') ||
        event.target.closest('.ap-lookbook-gallery__modal-media') ||
        event.target.closest('.ap-lookbook-gallery__modal-caption')
      ) {
        return;
      }
      // Clicked the empty space around the look → dismiss.
      this.#close();
    });

    this.modal.querySelectorAll('[data-ap-lookbook-arrow]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.#go(button.getAttribute('data-ap-lookbook-arrow') === 'next' ? 1 : -1);
      });
    });

    this.modal.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#go(-1);
      }
    });

    this.modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close();
    });

    this.modal.addEventListener('close', () => {
      this.isClosing = false;
      this.modal.classList.remove('dialog-closing');
      this.#abort(); // cancel any in-flight fade/slide so a stale timer can't fire after close
      this.#unlockBody();
      if (this.trigger && typeof this.trigger.focus === 'function') {
        this.trigger.focus({ preventScroll: true });
      }
      this.trigger = null;
    });
  }

  #bindSwipe() {
    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let railWidth = 1;
    let active = false;
    let decided = false;
    let horizontal = false;

    const onDown = (event) => {
      if (this.total < 2 || this.isAnimating) return;
      // Mouse never drag-navigates; on fade (desktop/arrow) viewports there's no swipe at all.
      if (event.pointerType === 'mouse' && (event.button !== 0 || this.#isFadeMode())) return;
      active = true;
      decided = false;
      horizontal = false;
      startX = event.clientX;
      startY = event.clientY;
      deltaX = 0;
      railWidth = this.rail.offsetWidth || 1;
    };

    const onMove = (event) => {
      if (!active) return;
      deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      if (!decided) {
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;
        decided = true;
        horizontal = Math.abs(deltaX) > Math.abs(deltaY);
        if (!horizontal) {
          active = false;
          return;
        }
        // Live drag-translate only when sliding; fade viewports just detect the gesture.
        if (!this.#isFadeMode()) {
          this.rail.style.transition = 'none';
          try {
            this.rail.setPointerCapture(event.pointerId);
          } catch (error) {
            /* setPointerCapture can throw if the pointer is already released */
          }
        }
      }

      if (!horizontal || this.#isFadeMode()) return;
      event.preventDefault();
      this.rail.style.transform = `translate3d(calc(-100% + ${deltaX}px), 0, 0)`;
    };

    const onUp = () => {
      if (!active) return;
      active = false;
      if (!horizontal) return;

      const slideMode = !this.#isFadeMode();
      if (slideMode) this.rail.style.transition = '';
      const threshold = Math.min(80, railWidth * 0.15);

      if (deltaX <= -threshold) {
        this.#go(1);
      } else if (deltaX >= threshold) {
        this.#go(-1);
      } else if (slideMode) {
        this.#snapBack();
      }
    };

    this.rail.addEventListener('pointerdown', onDown);
    this.rail.addEventListener('pointermove', onMove);
    this.rail.addEventListener('pointerup', onUp);
    this.rail.addEventListener('pointercancel', onUp);
  }

  // ---- page scroll lock ---------------------------------------------------

  #lockBody() {
    if (this.bodyLocked) return;
    this.rootHadScrollLock = document.documentElement.hasAttribute('scroll-lock');
    document.documentElement.setAttribute('scroll-lock', '');
    this.bodyLocked = true;
  }

  #unlockBody() {
    if (!this.bodyLocked) return;
    if (!this.rootHadScrollLock) {
      document.documentElement.removeAttribute('scroll-lock');
    }
    this.rootHadScrollLock = false;
    this.bodyLocked = false;
  }
}

if (!customElements.get('ap-lookbook-gallery')) {
  customElements.define('ap-lookbook-gallery', ApLookbookGallery);
}
